import React, { useState, useEffect } from 'react';
import { Ticket, AlertCircle, Gift, TrendingUp, Clock, CheckCircle, XCircle } from 'lucide-react';
import { ApiService } from '../../../services/api';
import { Voucher } from '../../../types/api';

const VoucherManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'available' | 'used' | 'expired'>('available');
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchVouchers();
  }, [activeTab, page, pageSize]);

  const fetchVouchers = async () => {
    try {
      setLoading(true);
      const status = activeTab === 'available' ? 'unused' : activeTab;
      const response = await ApiService.getVouchers({ 
        page, 
        pageSize, 
        status: status as 'unused' | 'used' | 'expired'
      });
      setVouchers(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch vouchers:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status: Voucher['status']) => {
    switch (status) {
      case 'unused':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'used':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: Voucher['status']) => {
    switch (status) {
      case 'unused':
        return '未使用';
      case 'used':
        return '已使用';
      case 'expired':
        return '已过期';
      default:
        return '未知';
    }
  };

  const getStatusIcon = (status: Voucher['status']) => {
    switch (status) {
      case 'unused':
        return <CheckCircle className="w-4 h-4" />;
      case 'used':
        return <Clock className="w-4 h-4" />;
      case 'expired':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50 flex justify-center items-center">
        <div className="flex items-center space-x-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
          <div className="text-xl font-medium text-gray-700">加载代金券信息中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-r from-emerald-600 via-blue-600 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl transform hover:scale-105 transition-transform">
                <Ticket className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                <Gift className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-emerald-600 to-purple-600 bg-clip-text text-transparent mb-4">
            代金券管理
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            管理您的代金券，查看使用记录和有效期
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">可用代金券</p>
                <p className="text-3xl font-bold text-emerald-600 group-hover:scale-110 transition-transform">
                  {vouchers.filter(v => v.status === 'unused').length}
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">已使用</p>
                <p className="text-3xl font-bold text-blue-600 group-hover:scale-110 transition-transform">
                  {vouchers.filter(v => v.status === 'used').length}
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Clock className="w-7 h-7 text-white" />
              </div>
            </div>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">已过期</p>
                <p className="text-3xl font-bold text-red-600 group-hover:scale-110 transition-transform">
                  {vouchers.filter(v => v.status === 'expired').length}
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <XCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">总价值</p>
                <p className="text-3xl font-bold text-purple-600 group-hover:scale-110 transition-transform">¥3,200</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* 使用说明 */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 shadow-lg">
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-3">代金券使用说明</h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  代金券可用于抵扣计算资源费用
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  部分代金券可能有使用限制，如最低消费金额或特定资源类型
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  请在有效期内使用，过期代金券将自动失效
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  每次结算时系统将自动选择最优惠的代金券组合
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* 标签页 */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-2 border border-white/20 shadow-xl">
          <nav className="flex space-x-2">
            <button
              onClick={() => setActiveTab('available')}
              className={`
                flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all
                ${activeTab === 'available'
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }
              `}
            >
              可用代金券
            </button>
            <button
              onClick={() => setActiveTab('used')}
              className={`
                flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all
                ${activeTab === 'used'
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }
              `}
            >
              已使用
            </button>
            <button
              onClick={() => setActiveTab('expired')}
              className={`
                flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all
                ${activeTab === 'expired'
                  ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }
              `}
            >
              已过期
            </button>
          </nav>
        </div>

        {/* 代金券卡片列表 */}
        <div className="space-y-4">
          {vouchers.map((voucher) => (
            <div key={voucher.id} className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all group">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                    voucher.status === 'unused' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' :
                    voucher.status === 'used' ? 'bg-gradient-to-r from-blue-500 to-indigo-500' :
                    'bg-gradient-to-r from-red-500 to-pink-500'
                  }`}>
                    {getStatusIcon(voucher.status)}
                    <div className="text-white">
                      <Ticket className="w-8 h-8" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{voucher.code}</h3>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(voucher.status)}`}>
                        {getStatusText(voucher.status)}
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 mb-2">
                      {voucher.type === 'fixed' 
                        ? `¥${voucher.amount}`
                        : `${voucher.amount}% 折扣`
                      }
                    </div>
                    <p className="text-gray-600 mb-3">{voucher.description}</p>
                    <div className="flex items-center text-sm text-gray-500 space-x-4">
                      <span>有效期：{voucher.validFrom} 至 {voucher.validTo}</span>
                    </div>
                  </div>
                </div>
                
                {voucher.restrictions && voucher.restrictions.length > 0 && (
                  <div className="ml-4">
                    <div className="text-xs text-gray-500 mb-1">使用限制</div>
                    <div className="space-y-1">
                      {voucher.restrictions.map((restriction, index) => (
                        <div key={index} className="flex items-center text-xs text-gray-600">
                          <span className="w-1 h-1 bg-gray-400 rounded-full mr-2"></span>
                          {restriction}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {vouchers.length === 0 && (
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-16 border border-white/20 shadow-xl text-center">
              <Ticket className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">暂无代金券</h3>
              <p className="text-gray-600">
                {activeTab === 'available' && '您当前没有可用的代金券'}
                {activeTab === 'used' && '您还没有使用过代金券'}
                {activeTab === 'expired' && '您没有已过期的代金券'}
              </p>
            </div>
          )}
        </div>

        {/* 分页 */}
        {total > pageSize && (
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 border border-white/20 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">每页显示</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
                <span className="text-sm text-gray-700">条记录</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">
                  第 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} 条，共 {total} 条
                </span>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    首页
                  </button>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(Math.ceil(total / pageSize), p + 1))}
                    disabled={page >= Math.ceil(total / pageSize)}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                  <button
                    onClick={() => setPage(Math.ceil(total / pageSize))}
                    disabled={page >= Math.ceil(total / pageSize)}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    末页
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoucherManagement;
